/**
 * NoteButton Component
 *
 * Standalone "Add Note" button for action bars.
 * Selection-dependent - requires at least one row selected.
 *
 * Provides a dedicated button for adding notes, as an alternative to
 * having "Add Note" buried inside the Annotate dropdown.
 *
 * Usage:
 * ```tsx
 * <NoteButton onClick={() => openNoteModal()} />
 * ```
 *
 * @module components/actions
 */

import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

export interface NoteButtonProps {
  /** Handler for note button click */
  onClick: () => void;
  /** Disable the button when no items selected */
  disabled?: boolean;
  /** Custom label (defaults to "Add Note") */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
}

/**
 * NoteButton Component
 *
 * Selection-dependent note button that matches the visual style of action bar.
 * Used across multiple tables (Assets, Vulnerabilities, Technologies, Seeds).
 *
 * @param props - Component props
 * @returns Note button JSX
 *
 * @example
 * ```tsx
 * <NoteButton
 *   onClick={() => modalHandlers.openNoteModal()}
 *   disabled={!hasSelection}
 *   data-testid="technology-note-button"
 * />
 * ```
 */
export function NoteButton({
  onClick,
  disabled = false,
  label = 'Add Note',
  className,
  'data-testid': testId = 'note-button',
}: NoteButtonProps) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outline"
      size="sm"
      className={cn('rounded-full', className)}
      data-testid={testId}
      aria-label={label}
    >
      <ChatBubbleBottomCenterTextIcon className="size-4" />
      {label}
    </Button>
  );
}
